/**
 * Multi-step wizard for creating a new user-generated report.
 *
 * Steps:
 *   0 – Report Info       (title, description)
 *   1 – Data & Columns    (data source, group-by columns ≤3, filter fields)
 *
 * Charts and stat cards are added after creation via the report settings panel.
 */

import { useState, useMemo, useEffect, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import DevicesIcon from "@mui/icons-material/Devices";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PeopleIcon from "@mui/icons-material/People";

import { useCreateUserGeneratedReport } from "@hooks/use-create/report-user-generated";
import { useDeleteUserGeneratedReport } from "@hooks/use-delete/report-user-generated";
import { useGetAggregateAssets } from "@hooks/use-get/aggregate-assets";
import { countDistinctAggregateValues } from "@hooks/use-get/aggregate-assets/utils";
import type { WpFieldConfig } from "@hooks/use-config/types";
import { getFieldInputType } from "@hooks/use-config/utils";
import { useAssetsConfigContext } from "@hooks/use-config-context/asset";
import { useTicketConfigContext } from "@hooks/use-config-context/ticket";
import { useUserConfigContext } from "@hooks/use-config-context/user";
import { underscoreToTitleCase } from "@utils/index";
import { TableCellInputType } from "@components/data-table/types";
import { EXCLUDED_REPORT_COLUMNS } from "../reporting-constants";
import { isDateField } from "@hooks/use-reports/utils/date-utils";
import type { UserFieldConfig } from "@hooks/use-config/user";

// ─── Types ────────────────────────────────────────────────────────────────────

type DataSourceType = "Assets" | "Tickets" | "Users";

const MAX_REPORT_COLUMN_OPTIONS = 50;

type SelectableColumn = {
  id: string;
  label: string;
};

type ColumnOptionCountMap = Partial<Record<DataSourceType, Record<string, number>>>;

const buildSelectableColumns = (
  config: Record<string, WpFieldConfig & { display?: boolean }> | undefined
): SelectableColumn[] => {
  if (!config || Object.keys(config).length === 0) {
    return [];
  }

  return Object.entries(config)
    .filter(([key, field]) => {
      if (EXCLUDED_REPORT_COLUMNS.has(key)) {
        return false;
      }

      if (field?.display === false || !field?.name) {
        return false;
      }

      const inputType = getFieldInputType(field);
      return ![
        TableCellInputType.Form,
        TableCellInputType.UserInput,
      ].includes(inputType);
    })
    .map(([key, field]) => ({
      id: key,
      label: field.name || underscoreToTitleCase(key),
    }));
};

/** Returns columns where `field.type === "number"` — eligible for sum aggregation. */
const buildNumericColumns = (
  config: Record<string, WpFieldConfig & { display?: boolean }> | undefined
): SelectableColumn[] => {
  if (!config || Object.keys(config).length === 0) return [];
  return Object.entries(config)
    .filter(([key, field]) => !EXCLUDED_REPORT_COLUMNS.has(key) && field?.type === "number" && field?.name)
    .map(([key, field]) => ({ id: key, label: field.name || underscoreToTitleCase(key) }));
};

interface WizardFormState {
  title: string;
  description: string;
  dataSource: DataSourceType;
  groupByColumns: string[];
  sumColumns: string[];
  filterColumns: string[];
}

const EMPTY_FORM: WizardFormState = {
  title: "",
  description: "",
  dataSource: "Assets",
  groupByColumns: [],
  sumColumns: [],
  filterColumns: [],
};

const STEPS = ["Report Info", "Data & Columns"];

const buildSelectableUserColumns = (
  config: Record<string, UserFieldConfig> | undefined
): SelectableColumn[] => {
  if (!config || Object.keys(config).length === 0) {
    return [];
  }

  return Object.entries(config)
    .filter(([key, field]) => {
      if (EXCLUDED_REPORT_COLUMNS.has(key)) {
        return false;
      }

      return Boolean(field.group || field.filter);
    })
    .map(([key, field]) => ({
      id: key,
      label: field.name || underscoreToTitleCase(key),
    }));
};

const buildNumericUserColumns = (
  config: Record<string, UserFieldConfig> | undefined
): SelectableColumn[] => {
  if (!config || Object.keys(config).length === 0) {
    return [];
  }

  return Object.entries(config)
    .filter(([key, field]) => !EXCLUDED_REPORT_COLUMNS.has(key) && field.type === "number")
    .map(([key, field]) => ({
      id: key,
      label: field.name || underscoreToTitleCase(key),
    }));
};

export interface CreateReportWizardInitialData {
  reportId: string;
  title: string;
  description?: string;
  dataSource?: "Assets" | "Tickets" | "Users";
  groupByColumns?: string[];
  sumColumns?: string[];
  filterColumns?: string[];
}

export interface CreateReportWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated: (newReportId: string) => void;
  /** When provided the wizard operates in edit mode */
  initialData?: CreateReportWizardInitialData;
  onUpdated?: () => void;
  /** Controlled by the /features API — hides Users data source when false */
  userExtensionEnabled?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CreateReportWizard = ({
  open,
  onClose,
  onCreated,
  initialData,
  onUpdated,
  userExtensionEnabled = false,
}: CreateReportWizardProps) => {
  const isEditing = Boolean(initialData?.reportId);

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<WizardFormState>(() =>
    initialData
      ? {
          title: initialData.title,
          description: initialData.description ?? "",
          dataSource: initialData.dataSource ?? "Assets",
          groupByColumns: initialData.groupByColumns ?? [],
          sumColumns: initialData.sumColumns ?? [],
          filterColumns: initialData.filterColumns ?? [],
        }
      : EMPTY_FORM
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Re-seed form whenever initialData changes (e.g. opening wizard for a different report)
  useEffect(() => {
    if (open) {
      setForm(
        initialData
          ? {
              title: initialData.title,
              description: initialData.description ?? "",
              dataSource: initialData.dataSource ?? "Assets",
              groupByColumns: initialData.groupByColumns ?? [],
              sumColumns: initialData.sumColumns ?? [],
              filterColumns: initialData.filterColumns ?? [],
            }
          : EMPTY_FORM
      );
      setStep(0);
      setErrors({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { createReport, loading: creating, error: createError } =
    useCreateUserGeneratedReport();
  const { deleteReport: deleteUserGeneratedReport } = useDeleteUserGeneratedReport();
  const { getAggregateAssets } = useGetAggregateAssets();

  const { config: assetsConfig, loading: assetsLoading } = useAssetsConfigContext();
  const { config: ticketsConfig, loading: ticketsLoading } = useTicketConfigContext();
  const { config: usersConfig, loading: usersLoading } = useUserConfigContext();

  const configLoading = assetsLoading || ticketsLoading || usersLoading;
  const [columnOptionCounts, setColumnOptionCounts] =
    useState<ColumnOptionCountMap>({ Assets: {}, Tickets: {}, Users: {} });
  const [columnOptionCountsLoading, setColumnOptionCountsLoading] = useState<
    Record<DataSourceType, boolean>
  >({ Assets: false, Tickets: false, Users: false });
  const prefetchedColumnsKeyRef = useRef<Partial<Record<DataSourceType, string>>>({});

  const columnsByDataSource = useMemo(
    () => ({
      Assets: buildSelectableColumns(assetsConfig),
      Tickets: buildSelectableColumns(ticketsConfig),
      Users: buildSelectableUserColumns(usersConfig),
    }),
    [assetsConfig, ticketsConfig, usersConfig]
  );

  const numericColumnsByDataSource = useMemo(
    () => ({
      Assets: buildNumericColumns(assetsConfig),
      Tickets: buildNumericColumns(ticketsConfig),
      Users: buildNumericUserColumns(usersConfig),
    }),
    [assetsConfig, ticketsConfig, usersConfig]
  );

  const availableColumns = columnsByDataSource[form.dataSource];
  const numericColumns = numericColumnsByDataSource[form.dataSource];

  useEffect(() => {
    if (!open || configLoading) {
      return;
    }

    // Only prefetch counts for the data source currently visible in the wizard —
    // switching the ToggleButton (handleDataSourceChange) re-runs this effect via
    // the form.dataSource dep, prefetching the newly selected source on demand
    // instead of firing one aggregate request per column across all 3 sources
    // (Assets+Tickets+Users) on every wizard open, regardless of what's shown.
    const dataSources: DataSourceType[] = [form.dataSource];

    dataSources.forEach((dataSource) => {
      const columns = columnsByDataSource[dataSource];
      const columnsKey = columns.map((column) => column.id).join(",");

      if (!columnsKey || prefetchedColumnsKeyRef.current[dataSource] === columnsKey) {
        return;
      }

      prefetchedColumnsKeyRef.current[dataSource] = columnsKey;
      setColumnOptionCountsLoading((prev) => ({ ...prev, [dataSource]: true }));

      Promise.allSettled(
        columns.map(async ({ id }) => {
          const result = await getAggregateAssets({
            groupBy: [id],
            postType: dataSource === "Assets" ? "asset" : dataSource === "Tickets" ? "ticket" : "user",
          });

          return {
            id,
            count: countDistinctAggregateValues(result.data, id),
          };
        })
      )
        .then((results) => {
          const nextCounts = results.reduce<Record<string, number>>((acc, result, i) => {
            if (result.status === "fulfilled") {
              acc[result.value.id] = result.value.count;
            } else {
              // Backend rejected this field — treat as exceeding the limit so it stays hidden
              acc[columns[i].id] = MAX_REPORT_COLUMN_OPTIONS + 1;
            }

            return acc;
          }, {});

          setColumnOptionCounts((prev) => ({
            ...prev,
            [dataSource]: nextCounts,
          }));
        })
        .finally(() => {
          setColumnOptionCountsLoading((prev) => ({ ...prev, [dataSource]: false }));
        });
    });
  }, [columnsByDataSource, configLoading, form.dataSource, getAggregateAssets, open]);

  const selectableColumns = useMemo(() => {
    const optionCounts = columnOptionCounts[form.dataSource] ?? {};
    const selectedColumnIds = new Set([...form.groupByColumns, ...form.filterColumns]);

    return availableColumns.filter((column) => {
      const distinctOptionCount = optionCounts[column.id];

      if (selectedColumnIds.has(column.id) || distinctOptionCount === undefined) {
        return true;
      }

      // Date fields are always included regardless of distinct value count
      if (isDateField(column.id)) {
        return true;
      }

      return distinctOptionCount <= MAX_REPORT_COLUMN_OPTIONS;
    });
  }, [availableColumns, columnOptionCounts, form.dataSource, form.filterColumns, form.groupByColumns]);

  const hiddenColumnCount = Math.max(availableColumns.length - selectableColumns.length, 0);
  const isCurrentDataSourcePrefetching = columnOptionCountsLoading[form.dataSource];

  // ── Validation ──────────────────────────────────────────────────────────────

  const validate = (s: number): boolean => {
    const errs: Record<string, string> = {};
    if (s === 0 && !form.title.trim()) {
      errs.title = "Report title is required";
    }
    if (s === 1 && form.groupByColumns.length === 0) {
      errs.groupBy = "Select at least one group-by column";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Navigation ──────────────────────────────────────────────────────────────

  const handleNext = () => {
    if (validate(step)) setStep((s) => s + 1);
  };

  const handleBack = () => {
    setStep((s) => s - 1);
    setErrors({});
  };

  const handleClose = () => {
    setStep(0);
    setForm(EMPTY_FORM);
    setErrors({});
    onClose();
  };

  // ── Submit ───────────────────────────────────────────────────────────────────

  /** Builds the base export table config from the current form state. */
  const buildExportTable = () => ({
    id: "exportTable",
    type: "export-table" as const,
    dimensions: [...form.groupByColumns, ...(form.sumColumns.length > 0 ? form.sumColumns : []), "count"],
    title: `${form.title.trim()} — Data Table`,
    reportName: form.title.trim(),
    exportFilename: `${form.title.trim().toLowerCase().replace(/\s+/g, "-")}.csv`,
    width: "full" as const,
  });

  const handleCreate = async () => {
    if (!validate(step)) return;

    if (isEditing && initialData) {
      // Restructure mode — delete the existing report entirely, then recreate it
      // from scratch with the new data source. This guarantees a clean slate with
      // no stale override config, charts, or stat cards.
      const deleted = await deleteUserGeneratedReport(initialData.reportId);
      if (!deleted) return;

      const result = await createReport({
        title: form.title.trim(),
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
        admin_config: {
          dataSources: [
            {
              id: "primary",
              dbTable: form.dataSource,
              groupBy: form.groupByColumns,
              ...(form.sumColumns.length > 0 ? { sum: form.sumColumns } : {}),
            },
          ],
          charts: [buildExportTable()],
        },
        ...(form.filterColumns.length > 0
          ? { report_filters: form.filterColumns }
          : {}),
      });
      if (result) {
        handleClose();
        onCreated(String(result.report_id ?? result.id ?? ""));
      }
      return;
    }

    // Create mode
    const result = await createReport({
      title: form.title.trim(),
      ...(form.description.trim() ? { description: form.description.trim() } : {}),
      admin_config: {
        dataSources: [
          {
            id: "primary",
            dbTable: form.dataSource,
            groupBy: form.groupByColumns,
            ...(form.sumColumns.length > 0 ? { sum: form.sumColumns } : {}),
          },
        ],
        charts: [buildExportTable()],
      },
      ...(form.filterColumns.length > 0
        ? { report_filters: form.filterColumns }
        : {}),
    });

    if (result) {
      handleClose();
      onCreated(String(result.report_id ?? result.id ?? ""));
    }
  };

  // ── Form helpers ─────────────────────────────────────────────────────────────

  const handleDataSourceChange = (val: DataSourceType | null) => {
    if (!val) return;
    setForm((f) => ({
      ...f,
      dataSource: val,
      groupByColumns: [],
      sumColumns: [],
      filterColumns: [],
    }));
  };

  const toggleSum = (id: string) => {
    setForm((prev) => {
      const on = prev.sumColumns.includes(id);
      return { ...prev, sumColumns: on ? prev.sumColumns.filter((c) => c !== id) : [...prev.sumColumns, id] };
    });
  };

  const toggleGroupBy = (id: string) => {
    setForm((prev) => {
      const on = prev.groupByColumns.includes(id);
      const next = on
        ? prev.groupByColumns.filter((c) => c !== id)
        : prev.groupByColumns.length < 3
        ? [...prev.groupByColumns, id]
        : prev.groupByColumns;
      return { ...prev, groupByColumns: next };
    });
    setErrors((e) => ({ ...e, groupBy: "" }));
  };

  const toggleFilter = (id: string) => {
    setForm((prev) => {
      const on = prev.filterColumns.includes(id);
      return {
        ...prev,
        filterColumns: on
          ? prev.filterColumns.filter((c) => c !== id)
          : [...prev.filterColumns, id],
      };
    });
  };

  const isLast = step === STEPS.length - 1;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Create New Report</DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* ── Step 0: Report Info ─────────────────────────────────────── */}
          {step === 0 && (
            <Stack spacing={2.5}>
              <TextField
                label="Report Title"
                value={form.title}
                onChange={(e) => {
                  setForm((f) => ({ ...f, title: e.target.value }));
                  setErrors((ev) => ({ ...ev, title: "" }));
                }}
                error={!!errors.title}
                helperText={errors.title}
                required
                fullWidth
                autoFocus
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                multiline
                rows={3}
                fullWidth
                placeholder="Optional — briefly describe what this report shows"
              />
            </Stack>
          )}

          {/* ── Step 1: Data & Columns ──────────────────────────────────── */}
          {step === 1 && (
            <Stack spacing={3}>
              {/* Data Source */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Data Source
                </Typography>
                <ToggleButtonGroup
                  value={form.dataSource}
                  exclusive
                  onChange={(_, val) => handleDataSourceChange(val)}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="Assets">
                    <DevicesIcon fontSize="small" sx={{ mr: 0.75 }} />
                    Assets
                  </ToggleButton>
                  <ToggleButton value="Tickets">
                    <ConfirmationNumberIcon fontSize="small" sx={{ mr: 0.75 }} />
                    Tickets
                  </ToggleButton>
                  {userExtensionEnabled && (
                    <ToggleButton value="Users">
                      <PeopleIcon fontSize="small" sx={{ mr: 0.75 }} />
                      Users
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              </Box>

              {/* Group By */}
              <Box>
                <Typography variant="subtitle2">
                  Group By{" "}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    (select up to 3 columns)
                  </Typography>
                </Typography>
                {configLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">Loading columns…</Typography>
                  </Box>
                ) : isCurrentDataSourcePrefetching ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">
                      Preparing group-by options…
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}
                  >
                    {selectableColumns.map((col) => {
                      const selected = form.groupByColumns.includes(col.id);
                      const maxed = !selected && form.groupByColumns.length >= 3;
                      return (
                        <Chip
                          key={col.id}
                          label={col.label}
                          onClick={() => !maxed && toggleGroupBy(col.id)}
                          color={selected ? "primary" : "default"}
                          variant={selected ? "filled" : "outlined"}
                          disabled={maxed}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
                {!configLoading && !isCurrentDataSourcePrefetching && hiddenColumnCount > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                    {hiddenColumnCount} columns hidden because they have more than {MAX_REPORT_COLUMN_OPTIONS} distinct values.
                  </Typography>
                )}
                {errors.groupBy && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {errors.groupBy}
                  </Typography>
                )}
              </Box>

              {/* Sum Fields — only shown when numeric columns exist */}
              {numericColumns.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">
                    Sum Fields{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      (optional — numeric fields to aggregate by total)
                    </Typography>
                  </Typography>
                  {configLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption">Loading columns…</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                      {numericColumns.map((col) => {
                        const selected = form.sumColumns.includes(col.id);
                        return (
                          <Chip
                            key={col.id}
                            label={col.label}
                            onClick={() => toggleSum(col.id)}
                            color={selected ? "primary" : "default"}
                            variant={selected ? "filled" : "outlined"}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

              {/* Filter Menu Options */}
              <Box>
                <Typography variant="subtitle2">
                  Filter Menu Options{" "}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    (optional — columns users can interactively filter when viewing the report)
                  </Typography>
                </Typography>
                {configLoading || isCurrentDataSourcePrefetching ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">Preparing filter options…</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                    {selectableColumns.map((col) => {
                      const selected = form.filterColumns.includes(col.id);
                      return (
                        <Chip
                          key={col.id}
                          label={col.label}
                          onClick={() => toggleFilter(col.id)}
                          color={selected ? "secondary" : "default"}
                          variant={selected ? "filled" : "outlined"}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Stack>
          )}

          {createError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {createError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={creating}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          {step > 0 && (
            <Button onClick={handleBack} disabled={creating}>
              Back
            </Button>
          )}
          {isLast ? (
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={creating}
              startIcon={
                creating ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
            >
              {isEditing
                ? (creating ? "Restructuring…" : "Restructure Report")
                : (creating ? "Creating…" : "Create Report")}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </>
  );
};
